import { RecentlyViewedAccountsType, SettingsStorageType } from './types'
import { readCookie, setCookie } from '@signal/utils/cookies'

const PURECARS_SETTINGS_COOKIE = 'purecars-settings'
const PURECARS_DOMAIN = 'purecars.com'
const SECONDS_IN_A_YEAR = 31536000
const ACCOUNT_VALUE_SEPARATOR = '\u001F' // ASCII Unit Separator

/**
 * this function updates the global Kubical UI settings cookie with the given settings data
 * @param {Partial<SettingsStorageType>} settingsData - the settings data to update.
 *   Keys not included in SettingsStorageType will not be changed
 */
export function setSettingsStorage(
  settingsData: Partial<SettingsStorageType>
): void {
  let currentSettings = {}
  const pureCarsStorage = readCookie(PURECARS_SETTINGS_COOKIE) || '{}'
  if (pureCarsStorage) {
    try {
      currentSettings = JSON.parse(
        decodeURIComponent(window.atob(pureCarsStorage))
      )
    } catch (err) {}
  }

  setCookie({
    name: PURECARS_SETTINGS_COOKIE,
    value: window.btoa(
      encodeURIComponent(
        JSON.stringify({
          ...currentSettings,
          ...settingsData
        })
      )
    ),
    domain: PURECARS_DOMAIN,
    decode: false,
    ttl: SECONDS_IN_A_YEAR
  })
}

/**
 * this function gets existing data from the Kubical UI settings storage cookie
 * @param {Boolean} decodeBase64 - If true, then we should try to decode the settings cookie as Base64
 * @return {SettingsStorageType | undefined} - the existing settings data, or undefined if none
 */
export function getSettingsStorage(
  decodeBase64 = true
): SettingsStorageType | undefined {
  try {
    let pureCarsStorage = readCookie(PURECARS_SETTINGS_COOKIE)
    if (decodeBase64) {
      pureCarsStorage = window.atob(pureCarsStorage)
    }
    const parsedSettings = pureCarsStorage
      ? JSON.parse(decodeURIComponent(pureCarsStorage))
      : undefined
    if (!decodeBase64) {
      setSettingsStorage(parsedSettings)
    }
    return parsedSettings
  } catch (err) {
    if (decodeBase64) {
      return getSettingsStorage(false)
    }
    console.warn(
      'Unable to get settings from cookie!  Settings object was malformed.'
    )
  }
}

/**
 * this function gets the recently viewed accounts from the global Kubical UI settings storage cookie
 * @return {RecentlyViewedAccountsType} - the recently viewed accounts, or empty array if nothing was found in storage
 */
export function getRecentlyViewedAccountsFromStorage(): RecentlyViewedAccountsType {
  const settings = getSettingsStorage()
  if (!settings) {
    return []
  }

  if (settings.recentlyViewedAccounts?.length) {
    return settings.recentlyViewedAccounts
  }

  const values = settings.rv || undefined

  if (values?.length) {
    const parsed = values.map(val => {
      const [accountId, accountName] = val.split(ACCOUNT_VALUE_SEPARATOR)
      return { accountId, accountName }
    })

    return parsed
  }

  return []
}

/**
 * this function sets the recently viewed accounts in the global Kubical UI settings storage cookie
 * @param {RecentlyViewedAccountsType} recentlyViewedAccounts - The recently viewed accounts to set
 */
export function setRecentlyViewedAccountsInStorage(
  recentlyViewedAccounts: RecentlyViewedAccountsType
): void {
  setSettingsStorage({
    // setting to undefined drops the legacy field on the next JSON.stringify,
    // shrinking cookies that still carry the verbose array
    recentlyViewedAccounts: undefined,
    rv: recentlyViewedAccounts.map(
      ({ accountId, accountName }) =>
        `${accountId}${ACCOUNT_VALUE_SEPARATOR}${accountName}`
    )
  })
}
